/**
 * BulkTagModal — Modal fuer Tag-Setzen (mode='add') oder Tag-Entfernen (mode='remove').
 *
 * mode='add': Text-Input "Tag eingeben" (max 50 Zeichen).
 * mode='remove': Dropdown mit allen Tags die >= eine selektierte Karte hat.
 *   Tags werden aus store.leads gefiltert (nur leads in selectedLeadIds).
 *
 * Submit → store.runBulkAction({ leadIds, action, payload: { tag } })
 * Erfolg: Banner "{N} Karten aktualisiert", onClose nach 1.5s.
 * Fehler: rote Box "{N} fehlgeschlagen".
 *
 * CI-Farben: #E9FF57 + #1A1A1A. NIE weisse Schrift auf Gelb.
 */

import { useState } from 'react';
import { X } from 'lucide-react';
import { useLeadBoard } from '../store/useLeadBoard';
import type { BulkActionResponse } from '../services/leadsApi';

interface Props {
  mode: 'add' | 'remove';
  onClose: () => void;
}

export default function BulkTagModal({ mode, onClose }: Props) {
  const selectedLeadIds = useLeadBoard((s) => s.selectedLeadIds);
  const leads = useLeadBoard((s) => s.leads);
  const runBulkAction = useLeadBoard((s) => s.runBulkAction);

  const [tag, setTag] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<BulkActionResponse | null>(null);
  const [inputError, setInputError] = useState<string | null>(null);

  // Tags die >= eine selektierte Karte hat (fuer mode='remove' Dropdown)
  const selectedLeads = leads.filter((l) => selectedLeadIds.has(l.id));
  const availableTags = Array.from(
    new Set(selectedLeads.flatMap((l) => l.tags)),
  ).sort();

  const handleSubmit = async () => {
    const trimmedTag = tag.trim();
    if (!trimmedTag) {
      setInputError('Tag ist Pflicht');
      return;
    }
    if (trimmedTag.length > 50) {
      setInputError('Tag darf maximal 50 Zeichen haben');
      return;
    }
    setInputError(null);
    setBusy(true);
    try {
      const res = await runBulkAction({
        leadIds: Array.from(selectedLeadIds),
        action: mode === 'add' ? 'add_tag' : 'remove_tag',
        payload: { tag: trimmedTag },
      });
      setResult(res);
      // Automatisch schliessen nach 1.5s wenn kein Fehler
      if (res.failed.length === 0) {
        setTimeout(() => onClose(), 1500);
      }
    } catch (e: any) {
      setInputError(e?.message ?? 'Aktion fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const title = mode === 'add' ? 'Tag setzen' : 'Tag entfernen';

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded-lg max-w-sm w-full mx-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <header className="flex items-center justify-between p-4 border-b border-[#DAD9D9]">
          <h2 className="text-base font-semibold text-[#1A1A1A]">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-[#1A1A1A] rounded p-1"
            aria-label="Schliessen"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        {/* Body */}
        <div className="p-4 space-y-4">
          <p className="text-sm text-gray-600">
            Aktion wird auf <span className="font-semibold text-[#1A1A1A]">{selectedLeadIds.size}</span>{' '}
            {selectedLeadIds.size === 1 ? 'Karte' : 'Karten'} angewendet.
          </p>

          {mode === 'add' ? (
            <div>
              <label className="block text-sm font-medium text-[#1A1A1A] mb-1">
                Tag eingeben
              </label>
              <input
                type="text"
                value={tag}
                onChange={(e) => {
                  setTag(e.target.value);
                  setInputError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
                maxLength={50}
                placeholder="z.B. messe-2026"
                disabled={busy}
                className={
                  'w-full border rounded px-3 py-2 text-[#1A1A1A] text-sm ' +
                  (inputError ? 'border-red-500' : 'border-[#DAD9D9]')
                }
              />
              {inputError && (
                <p className="text-red-600 text-xs mt-1">{inputError}</p>
              )}
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-[#1A1A1A] mb-1">
                Tag auswaehlen
              </label>
              {availableTags.length === 0 ? (
                <p className="text-sm text-gray-500">
                  Keine gemeinsamen Tags bei den ausgewaehlten Karten.
                </p>
              ) : (
                <select
                  value={tag}
                  onChange={(e) => {
                    setTag(e.target.value);
                    setInputError(null);
                  }}
                  disabled={busy}
                  className={
                    'w-full border rounded px-3 py-2 text-[#1A1A1A] text-sm bg-white ' +
                    (inputError ? 'border-red-500' : 'border-[#DAD9D9]')
                  }
                >
                  <option value="">– Tag auswaehlen –</option>
                  {availableTags.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              )}
              {inputError && (
                <p className="text-red-600 text-xs mt-1">{inputError}</p>
              )}
            </div>
          )}

          {/* Erfolgs-Banner */}
          {result && result.failed.length === 0 && (
            <div className="bg-[#E9FF57]/30 border border-[#E9FF57] text-[#1A1A1A] px-3 py-2 rounded text-sm font-medium">
              {result.successful} {result.successful === 1 ? 'Karte' : 'Karten'} aktualisiert.
            </div>
          )}

          {/* Fehler-Banner */}
          {result && result.failed.length > 0 && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
              {result.successful > 0 && (
                <p>{result.successful} erfolgreich.</p>
              )}
              <p>{result.failed.length} fehlgeschlagen.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className="flex items-center justify-end gap-2 p-4 border-t border-[#DAD9D9]">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 text-sm rounded border border-[#DAD9D9] text-[#1A1A1A] hover:bg-gray-50 disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={busy || (mode === 'remove' && availableTags.length === 0)}
            className="px-4 py-2 text-sm rounded bg-[#E9FF57] text-[#1A1A1A] font-medium hover:opacity-90 disabled:opacity-50"
          >
            {busy ? 'Wird ausgefuehrt...' : title}
          </button>
        </footer>
      </div>
    </div>
  );
}
